import React from 'react';

import { Hub } from 'aws-amplify/utils';
import { fetchAuthSession, getCurrentUser, signInWithRedirect } from 'aws-amplify/auth';
import { inject, observer } from 'mobx-react';
import { GoogleOAuthProvider } from '@react-oauth/google';

import i18n from '@/content';
import {
    AUTO_SIGN_IN_INITIATED,
    FEDERATED_PROVIDER,
    MAP_ORIGIN_TO_LOCAL_STORAGE_KEY,
    NEXT_PAGE_AFTER_SIGN_IN,
    ORG_USER_SIGN_IN_INITIATED,
    RECIPIENT_EMAIL_FOR_LOGIN,
} from '@/consts';
import { JUST_LOGGED_IN_TIMESTAMP_KEY, JUST_LOGGED_IN_TIMEOUT_MS } from '@/components/MiniApps/Common/constants';
import { App } from '../App';
import AuthContainer from './AuthContainer/AuthContainer';
import { Loading } from '../Common/Loading';
import MiniAppsContainer from '../MiniApps';
import ErrorMessage from '../Common/ErrorMessage/ErrorMessage';
import CrossOriginAuthenticator from './CrossOriginAuthenticator';
import BlockingPopup from './BlockingPopup';
import { withRouter, WithRouterProps } from '@/components/HOC';
import {
    AppAuthenticatorProps, AuthHubCapsule, AuthHubPayload, CognitoAuthEvents,
} from './interfaces';
import { withTranslation } from './WithTranslation';
import ability, { tryUpdateRulesFromToken, WSO_RULES } from '../../config/ability';
import {
    AuthMessage,
    captureCognitoError,
    captureErrorForSentry,
    checkIsDeadSessionError,
    checkIsSignedIn,
    getAuthI18nPayload,
    parseAuthErrorType,
    parseCognitoTriggerErrorMessage,
    postAuthMessage,
    SubscribablePromise,
    subscribeOnAuthMessage,
    tryGetTimelyRedirectPath,
    toTitleCase,
} from '../utils';
import appConfig, {
    ALL_SSO, IS_LOCAL_ENV, IS_NON_PROD_ENV, SSOTypes,
} from '../../config/env';
import {
    ALLOWED_ROUTES_FOR_UNAUTHORIZED,
    AppRoutes,
    CROSS_ORIGIN_OAUTH,
    ExternalStorageRoutes,
    MINI_APPS_PATH_NAMES_LIST,
    MiniAppRoutes,
} from '@/config/appRoutes';
import {
    AuthStates,
    CognitoGroups,
    GdriveExportState,
    SyncCallback,
} from '@/types/types';
import { BASEURL, ENDPOINTS } from '@/api';
import configureAmplify, { clearCognitoSessionStorage } from '@/config/amplify';
import {
    checkIsSignInPath,
    getSingInQueryParam,
    getUrlParams,
    getWSOAuthErrors,
    handleAuthTopic,
    hashToSearch,
    refreshCognitoSession,
} from './helpers';
import styles from './AppAuthenticator.module.scss';
import { ConfigProvider } from 'antd';

const TOKEN_REFRESH_ATTEMPTS_LIMIT = 1;

const ORGANIZATIONS_NAMESPACE = 'organizations';

export const IS_TEST_MODE = new URLSearchParams(window.location.search).has('testMode');

interface AuthError {
    hasError: boolean;
    title: string;
    subtitle: string;
}

export interface AuthenticatorState {
    isLoading: boolean;
    isAutoSignPreviouslyInitiated: boolean;
    isRedirectToMainAppAllowed: boolean;
    WSOAuthError?: AuthError;
    clientId: string;
}

@inject('appStore')
@observer
class AppAuthenticator extends React.Component<AppAuthenticatorProps & WithRouterProps, AuthenticatorState> {
    constructor(props) {
        super(props);
        this.state = {
            isLoading: true,
            isAutoSignPreviouslyInitiated: false,
            isRedirectToMainAppAllowed: true,
            clientId: '',
            WSOAuthError: {
                hasError: false,
                title: '',
                subtitle: '',
            },
        };
        this.cognitoUserSetter = new SubscribablePromise<void>();
    }

    private readonly cognitoUserSetter: SubscribablePromise<void>;

    private tokenRefreshAttempts = 0;

    private authChannelDisposer: SyncCallback;

    /** Disposer returned by Hub.listen (v6 removed Hub.remove). */
    private hubDisposer: SyncCallback;

    /**
     * Set to true when tryRedirectToMiniApp has already scheduled a navigate via
     * setTimeout(0) and cleared the sessionStorage path. This prevents subsequent
     * componentDidUpdate cycles (triggered by unrelated state changes like isLoading)
     * from entering the else-if branch in runSignedInUserFlow and prematurely
     * unlocking isRedirectToMainAppAllowed before the navigate fires.
     * Reset to false on sign-out (clearUserData) and at the start of each new
     * login session (tryLockMainApp).
     */
    private pendingMiniAppRedirect = false;

    async componentDidMount(): Promise<void> {
        const { location, appStore } = this.props;
        const { IS_WSO } = appStore.authSettingsStore;
        this.authChannelDisposer = subscribeOnAuthMessage(this.storageListener);
        // TODO: uncomment it when organizations will be ready
        // this.runUserOrganizationsReaction();
        // Listen before configuring Amplify so OAuth/custom-auth Hub events
        // (signedIn / signInWithRedirect_failure) are not missed when the
        // redirect callback completes quickly on page load.
        this.hubDisposer = Hub.listen('auth', this.onCognitoAuthChange);

        // Configure Amplify regardless of IS_WSO. In WSO/Kerberos environments
        // the main user is authenticated via the IdP, but external recipients
        // landing on /shared-file?linkId=... still go through the OTP mini-app
        // (`OTPAuthSteps`), which calls Cognito's `Auth.signUp/.signIn`. Without
        // Amplify configured those calls throw `NoUserPoolError`.
        configureAmplify(true);

        if (IS_WSO) {
            await this.setWSOUser();
            // On-prem has no Cognito groups; the Administrators menu is still gated by
            // the same stage-level flag, resolved here via the `/ext/` API-key route.
            if (!ALLOWED_ROUTES_FOR_UNAUTHORIZED.includes(location.pathname as MiniAppRoutes)) {
                await this.tryLoadUIConfiguration();
            }
            this.stopLoading();
        } else {
            const searchParams = new URLSearchParams(location.search);
            const hasCode = searchParams.has('code');
            const errorDescription = searchParams.get('error_description');
            this.setUpBrowserStorage(searchParams, hasCode);
            this.tryLockMainApp();
            this.handleAutoSignIn();
            if (errorDescription) {
                this.handleCognitoAuthError(errorDescription);
                this.stopLoading();
            } else if (!hasCode) {
                await this.trySetUpInCognitoEnv();
            }
        }
    }

    componentDidUpdate(): void {
        const { location, appStore: { authSettingsStore, userStore } } = this.props;
        const { authState, isUserSetUp } = userStore;
        const searchParams = new URLSearchParams(location.search);

        const isSignInPage = checkIsSignInPath(location.pathname);
        const isOTPSignInPage = location.pathname === '/OTPSignIn';
        const { isAutoSignPreviouslyInitiated } = this.state;
        const isUserSignedIn = checkIsSignedIn(authState);
        const isUserSignedOut = authState === AuthStates.unauthenticated;
        const hasError = searchParams.has('error_description');
        const isAutoSignUncompleted = isUserSignedOut && isAutoSignPreviouslyInitiated && !hasError;
        if (isUserSignedIn && isUserSetUp && (isSignInPage || isOTPSignInPage)) {
            this.runSignedInUserFlow(searchParams);
        } else if (isAutoSignUncompleted && !authSettingsStore.HAS_API_PROXY) {
            this.showAllSSO();
        }
        if (isUserSignedOut && isSignInPage) {
            this.tryLockMainApp();
        }
    }

    componentWillUnmount(): void {
        this.authChannelDisposer?.();
        // Mirror componentDidMount: Hub.listen is registered unconditionally
        // (Cognito OTP recipient flow needs it even in WSO/Kerberos), so the
        // matching disposer must also run unconditionally to avoid listener leaks.
        // v6 removed Hub.remove; use the disposer returned by Hub.listen.
        this.hubDisposer?.();
    }

    storageListener = (message: AuthMessage): void => {
        const { appStore: { userStore: { authState } } } = this.props;
        handleAuthTopic(message, authState);
    }

    private setUpBrowserStorage(queryParams: URLSearchParams, hasCode: boolean): void {
        try {
            const { location } = this.props;
            const state = queryParams.get('state');
            const isGoogleDriveRedirect = location.pathname === ExternalStorageRoutes.gdrive;
            if (isGoogleDriveRedirect && !hasCode) {
                const stateObject: GdriveExportState = JSON.parse(state);
                if (stateObject.exportIds) {
                    stateObject.ids = stateObject.exportIds;
                    delete stateObject.exportIds;
                }
                sessionStorage.setItem(
                    MAP_ORIGIN_TO_LOCAL_STORAGE_KEY.googledrive,
                    JSON.stringify(stateObject),
                );
            }

            const isBoxRedirect = location.pathname === ExternalStorageRoutes.box;
            if (isBoxRedirect) {
                const boxUserId = queryParams.get('user_id');
                const boxFileIds = queryParams.get('ids');
                if (boxUserId && boxFileIds) {
                    sessionStorage.setItem(
                        MAP_ORIGIN_TO_LOCAL_STORAGE_KEY.box,
                        `{"userId": "${boxUserId}", "ids": ["${boxFileIds}"]}`,
                    );
                }
            }
        } catch (error) {
            console.log('Set up browser storage error', error);
            captureErrorForSentry(error, 'Apps.setUpBrowserStorage');
        }
    }

    private stopLoading(): void {
        this.setState({ isLoading: false });
    }

    private async tryCheckIPAccess(): Promise<void> {
        const { appStore } = this.props;
        const { API } = appStore.authSettingsStore;
        try {
            const { is_allowed: isAllowed } = await API.get(BASEURL.backend(), ENDPOINTS.checkIPAccess(), {});
            appStore.setIsIPAccessAllowed(isAllowed);
        } catch (error) {
            captureErrorForSentry(error, 'AppAuthenticator.tryCheckIPAccess');
            console.log('could not get user IP', error);
        }
    }

    /**
     * Resolve the stage-level UI configuration from configurations-service.
     * Loaded for all authenticated users so upload file-type filtering
     * applies everywhere.
     */
    private async tryLoadUIConfiguration(): Promise<void> {
        const { appStore } = this.props;
        const { API } = appStore.authSettingsStore;
        try {
            const { supportedUploadExtensions } = await API.get(
                BASEURL.backend(),
                ENDPOINTS.getUIConfiguration(),
                {},
            );
            appStore.setSupportedUploadExtensions(
                Array.isArray(supportedUploadExtensions) ? supportedUploadExtensions : null,
            );
        } catch (error) {
            captureErrorForSentry(error, 'AppAuthenticator.tryLoadUIConfiguration');
            console.log('could not get UI configuration', error);
        }
    }

    private async saveCognitoUser(cognitoUser): Promise<void> {
        const { appStore: { userStore } } = this.props;
        // v6: tokens live in the auth session, not on the user object.
        const { tokens } = await fetchAuthSession();
        const idToken = tokens?.idToken;
        if (idToken) {
            tryUpdateRulesFromToken(idToken);
            await userStore.setUserInformation(cognitoUser);
        } else {
            console.error('Trying to set user with empty session', cognitoUser);
        }
    }

    private async setCognitoUser(onReceiveUser?: (cognitoUser) => void | Promise<void>): Promise<void> {
        const { appStore: { userStore } } = this.props;
        await this.cognitoUserSetter.call(async () => {
            const cognitoUser = await getCurrentUser();
            await onReceiveUser?.(cognitoUser);
            userStore.setAuthState(AuthStates.authenticated);
            await this.saveCognitoUser(cognitoUser);
        });
    }

    private clearUserData(): void {
        const { appStore } = this.props;
        appStore.clear();
        ability.update([]);
        // Reset the pending-navigate flag so the next login session starts clean.
        // Do NOT touch isRedirectToMainAppAllowed here: a stray Amplify `signOut`
        // event (fired before `cognitoHostedUI` / `signIn` during SSO or session
        // cleanup) would reset the lock that tryLockMainApp just set, causing
        // shouldTryRedirect = false and leaving the app stuck on the loading screen.
        this.pendingMiniAppRedirect = false;
    }

    private static tryReloadUser(): void {
        const hasMultipleSSOOptions = appConfig.SSO.filter((type) => type !== SSOTypes.cognito).length > 1;
        if (IS_TEST_MODE || hasMultipleSSOOptions) {
            return;
        }
        const federatedProvider = localStorage.getItem(FEDERATED_PROVIDER) || getSingInQueryParam();
        if (!federatedProvider) {
            const error = new Error('tryReloadUser: no federated provider known, skipping retry');
            captureErrorForSentry(error, 'AppAuthenticator.tryReloadUser');
            return;
        }
        signInWithRedirect({ provider: { custom: toTitleCase(federatedProvider) } });
    }

    private handleCognitoAuthEvent({ event, data, message }: AuthHubPayload): void {
        const { appStore: { userStore: { authState } } } = this.props;
        console.log('auth event', event);
        console.log('auth state', authState);
        switch (event) {
            case CognitoAuthEvents.signedIn:
                this.trySetUpInCognitoEnv();
                break;
            case CognitoAuthEvents.signInWithRedirect_failure:
                if (IS_NON_PROD_ENV) {
                    console.error('auth redirect failure', data);
                }
                captureErrorForSentry(
                    (data as { error?: unknown })?.error ?? data,
                    'AppAuthenticator.signInWithRedirect_failure',
                );
                // Stale OAuth inflight data can emit this event even when Cognito
                // already issued tokens (e.g. OTP/custom auth on the same page).
                void this.trySetUpInCognitoEnv();
                setTimeout(() => AppAuthenticator.tryReloadUser(), 3000);
                break;
            case CognitoAuthEvents.signedOut:
                this.clearUserData();
                break;
            case CognitoAuthEvents.tokenRefresh:
                this.setCognitoUser();
                break;
            case CognitoAuthEvents.tokenRefresh_failure:
                this.handleCognitoTokenRefreshFailure({ data, event, message });
                break;
            default:
                console.log('no handlers for event:', event);
        }
    }

    private handleCognitoAuthError(errorDescription: string): void {
        const { appStore: { setErrorStatus, userStore } } = this.props;
        const errorType = parseAuthErrorType(errorDescription);
        if (errorType === 'oauthSignUp') {
            userStore.setDoubleAuthError(true);
        } else {
            let parsedErrorMessage = '';
            const triggerErrorMessage = parseCognitoTriggerErrorMessage(errorDescription);
            if (errorType) {
                const [key, options] = getAuthI18nPayload(errorType, errorDescription);
                parsedErrorMessage = i18n.t(key, options);
                // TODO: explore all kind of such errors on BE
            } else if (triggerErrorMessage) {
                parsedErrorMessage = triggerErrorMessage;
            } else if (!errorDescription.includes('User does not')) {
                parsedErrorMessage = i18n.t('auth.errors.serverIssue');
            }
            setErrorStatus(true, '', parsedErrorMessage || errorDescription);
            userStore.setAuthState(AuthStates.unauthenticated);
        }
    }

    onCognitoAuthChange = async ({ payload }: AuthHubCapsule): Promise<void> => {
        const urlParams = new URLSearchParams(window.location.search);
        const errorDescription = urlParams.get('error_description');
        this.handleCognitoAuthEvent(payload);
        if (errorDescription) {
            this.handleCognitoAuthError(errorDescription);
        }
    }

    onWSOAuthError = (error: unknown): void => {
        const { appStore: { userStore } } = this.props;
        userStore.setAuthState(AuthStates.unauthenticated);
        const { title, subtitle } = getWSOAuthErrors(error);
        this.setState({ WSOAuthError: { hasError: true, title, subtitle } });
        postAuthMessage({ type: 'signOut' });
    }

    /**
     * Sign in using Kerberos/SPNEGO authentication.
     * Unlike OAuth2, Kerberos doesn't use redirects - the browser handles
     * the Negotiate handshake transparently.
     */
    WSOSignIn = async (initPathName: string): Promise<void> => {
        const { navigate, appStore: { userStore } } = this.props;
        const urlParams = getUrlParams();
        const errorDescription = urlParams.get('error_description');
        userStore.setAuthState(AuthStates.loading);
        try {
            if (errorDescription) {
                throw new Error(errorDescription);
            }
            // Kerberos authentication - no redirects needed
            // The browser automatically handles the Negotiate handshake
            await userStore.login();
            ability.update(WSO_RULES);
            
            const savedRedirectPath = tryGetTimelyRedirectPath();
            const locationPath = savedRedirectPath || (initPathName !== '' ? initPathName : AppRoutes.sharedWithMe);
            if (savedRedirectPath) {
                sessionStorage.removeItem(NEXT_PAGE_AFTER_SIGN_IN);
            }
            navigate(locationPath);
        } catch (error) {
            this.onWSOAuthError(error);
        }
    }

    renewWSOToken = async (): Promise<void> => {
        const { appStore: { userStore } } = this.props;
        try {
            await userStore.renewToken();
            ability.update(WSO_RULES);
        } catch (error) {
            console.log('error while token renew', error);
            await userStore.onSilentRenewError();
            await userStore.login();
        }
    }

    /**
     * Set up WSO/Kerberos user session.
     * Checks for existing session and either renews token or initiates login.
     * For OIDC: handles the ?code= callback after WSO2 redirect.
     * For Kerberos: calls login() directly (no redirects).
     */
    private async setWSOUser(): Promise<void> {
        const { location, appStore: { userStore } } = this.props;

        const searchParams = new URLSearchParams(location.search);
        const hasCode = searchParams.has('code');

        if (hasCode) {
            try {
                await userStore.continueLogin();
                ability.update(WSO_RULES);
                postAuthMessage({ type: 'signIn' });
                return;
            } catch (error) {
                console.error('Failed to process OIDC callback:', error);
                this.onWSOAuthError(error);
                return;
            }
        }

        const user = await userStore.authService.getUser();
        if (user) {
            await this.renewWSOToken();
            const newUser = await userStore.authService.getUser();
            console.log('user object is ', newUser);
        } else {
            const initPathName = location.pathname;
            const isGettingFilePage = ALLOWED_ROUTES_FOR_UNAUTHORIZED.includes(initPathName as MiniAppRoutes);
            const shouldStartSignIn = !isGettingFilePage;
            if (shouldStartSignIn) {
                await this.WSOSignIn(initPathName);
            } else {
                // Recipient routes (/shared-file, etc.) don't trigger the IdP
                // sign-in. If the recipient already authenticated via Cognito
                // OTP earlier and the tokens are still valid in localStorage,
                // pick up that session here so they don't have to OTP again on
                // every reload. trySetUpInCognitoEnv silently sets the auth
                // state to unauthenticated when no Cognito session exists, in
                // which case the OTP mini-app takes over as before.
                await this.trySetUpInCognitoEnv();
            }
        }
        postAuthMessage({ type: 'signIn' });
    }

    private tryRedirectToMiniApp(searchParams: URLSearchParams, miniAppSavedURL: string): void {
        const { navigate, location } = this.props;
        const isOTPSignInPage = location.pathname === '/OTPSignIn';
        const { isRedirectToMainAppAllowed } = this.state;
        const shouldTryRedirect = searchParams.has('code') || !isRedirectToMainAppAllowed || isOTPSignInPage;

        if (shouldTryRedirect) {
            // Flag that a navigate is in-flight so runSignedInUserFlow's else-if
            // branch does not prematurely unlock isRedirectToMainAppAllowed while
            // the setTimeout(0) navigate is still pending.  Placing the flag here
            // (inside shouldTryRedirect) guarantees it is only set when a navigate
            // was actually scheduled — a stray Amplify signOut event can reset
            // isRedirectToMainAppAllowed before tryRedirectToMiniApp runs, which
            // makes shouldTryRedirect false; in that case no navigate is queued
            // and the flag must NOT be set so the caller's else-if can still unlock.
            this.pendingMiniAppRedirect = true;
            sessionStorage.removeItem(NEXT_PAGE_AFTER_SIGN_IN);

            // Set timestamp to prevent immediate auto-redirect after login (for recipient pages)
            const isRecipientPage = miniAppSavedURL.includes('/shared-file') || miniAppSavedURL.includes('/recipient/');

            if (isRecipientPage) {
                const timestamp = Date.now().toString();
                sessionStorage.setItem(JUST_LOGGED_IN_TIMESTAMP_KEY, timestamp);
                setTimeout(() => {
                    sessionStorage.removeItem(JUST_LOGGED_IN_TIMESTAMP_KEY);
                }, JUST_LOGGED_IN_TIMEOUT_MS);
            }

            // Use setTimeout to ensure routes are ready
            setTimeout(() => {
                navigate(miniAppSavedURL, { replace: true });
            }, 0);
        }
    }

    private async trySetUpInCognitoEnv(): Promise<void> {
        const { appStore: { userStore, organizationsStore }, location } = this.props;
        try {
            organizationsStore.setUp();
            await this.setCognitoUser();
            // Remove recipient email from sessionStorage after successful authentication
            sessionStorage.removeItem(RECIPIENT_EMAIL_FOR_LOGIN);
            postAuthMessage({ type: 'signIn' });
            const postSaveCalls: Promise<boolean | void>[] = [];
            const isAdministrator = userStore.userGroupsSet.has(CognitoGroups.Administrators);
            const hasIPCheck = appConfig.ENABLE_IP_RESTRICTION && isAdministrator;
            const isGoogleDriveRedirect = location.pathname === ExternalStorageRoutes.gdrive;
            // UI config (upload extension allowlist) for authenticated users.
            // Also load on Shared Workspace mini-app so recipient uploads get the picker filter.
            const shouldLoadUIConfiguration = (
                !MINI_APPS_PATH_NAMES_LIST.includes(location.pathname as MiniAppRoutes)
                || location.pathname === MiniAppRoutes.sharedWorkspace
            );
            if (shouldLoadUIConfiguration) {
                postSaveCalls.push(this.tryLoadUIConfiguration());
            }
            if (hasIPCheck && !isGoogleDriveRedirect) {
                const tryCheckIpPromise = this.tryCheckIPAccess();
                if (!MINI_APPS_PATH_NAMES_LIST.includes(location.pathname as MiniAppRoutes)) {
                    postSaveCalls.push(tryCheckIpPromise);
                }
            }
            await Promise.all(postSaveCalls);
        } catch (error) {
            if (IS_LOCAL_ENV) {
                console.log('auth error', error);
            }
            if (checkIsDeadSessionError(error)) {
                captureCognitoError(error, 'AppAuthenticator.deadSessionRestore');
                clearCognitoSessionStorage();
                this.clearUserData();
            }
            organizationsStore.clear();
            userStore.setAuthState(AuthStates.unauthenticated);
        }
        this.stopLoading();
    }

    private async tryLogoutOnRefreshFailure(authState: string, error: unknown): Promise<void> {
        const { appStore: { userStore } } = this.props;
        captureCognitoError(error, 'AppAuthenticator.refreshCognitoSession');
        if (checkIsDeadSessionError(error)) {
            clearCognitoSessionStorage();
            this.clearUserData();
            userStore.setAuthState(AuthStates.unauthenticated);
            return;
        }
        if (authState !== AuthStates.unauthenticated) {
            await userStore.cognitoSignOut();
        }
    }

    private async handleCognitoTokenRefreshFailure(payload: AuthHubPayload): Promise<void> {
        const { appStore: { userStore: { authState } } } = this.props;
        if (this.tokenRefreshAttempts < TOKEN_REFRESH_ATTEMPTS_LIMIT) {
            this.tokenRefreshAttempts += 1;
            try {
                if (IS_NON_PROD_ENV) {
                    console.log('before retry refresh, auth state is', authState);
                }
                await refreshCognitoSession();
                this.tokenRefreshAttempts = 0;
            } catch (error) {
                if (IS_NON_PROD_ENV) {
                    console.log('token refresh failure', error);
                    console.log('authState is', authState);
                }
                await this.tryLogoutOnRefreshFailure(authState, error);
            }
        } else {
            if (IS_NON_PROD_ENV) {
                console.log('token refresh attempts limit reached', payload);
            }
            await this.tryLogoutOnRefreshFailure(authState, payload);
        }
    }

    private handleAutoSignIn(): void {
        const isAutoSignInInitiated = !!sessionStorage.getItem(AUTO_SIGN_IN_INITIATED);
        sessionStorage.removeItem(AUTO_SIGN_IN_INITIATED);
        this.setState({
            isAutoSignPreviouslyInitiated: isAutoSignInInitiated,
        });
    }

    private tryLockMainApp(): void {
        const { location } = this.props;
        const { isRedirectToMainAppAllowed } = this.state;
        if (
            tryGetTimelyRedirectPath()
            && checkIsSignInPath(location.pathname)
            && isRedirectToMainAppAllowed
        ) {
            // Reset the pending flag so the new login session starts clean.
            this.pendingMiniAppRedirect = false;
            this.setState({ isRedirectToMainAppAllowed: false });
        }
    }

    private runSignedInUserFlow(searchParams: URLSearchParams): void {
        const { isRedirectToMainAppAllowed } = this.state;
        localStorage.removeItem(ORG_USER_SIGN_IN_INITIATED);
        const nextPageURL = tryGetTimelyRedirectPath();
        if (nextPageURL) {
            // pendingMiniAppRedirect is set inside tryRedirectToMiniApp only when
            // shouldTryRedirect is true (i.e. a navigate was actually queued).
            this.tryRedirectToMiniApp(searchParams, nextPageURL);
        } else if (!isRedirectToMainAppAllowed && !this.pendingMiniAppRedirect) {
            // Only unlock when there is genuinely no mini-app navigate in flight.
            this.setState({ isRedirectToMainAppAllowed: true });
        }
    }

    private showAllSSO(): void {
        const { navigate } = this.props;
        navigate(`signin?auth=${ALL_SSO.join(',')}`);
        this.setState({ isAutoSignPreviouslyInitiated: false });
    }

    render(): JSX.Element {
        // TODO: refactor these condition through the react-router
        const urlParams = new URLSearchParams(window.location.search);
        const { props } = this;
        const {
            location,
            appStore,
            navigate,
            ...authenticatorProps
        } = props;
        const {
            errorStatus,
            isFullSetUp,
            blockingPopup,
            authSettingsStore: { IS_WSO },
            userStore: { authState },
        } = appStore;
        const {
            WSOAuthError, isLoading: stateIsLoading, isAutoSignPreviouslyInitiated, isRedirectToMainAppAllowed, clientId
        } = this.state;
        const hasCode = urlParams.has('code');
        const hasRedirectToMiniAppPrecondition = checkIsSignInPath(location.pathname) && !isRedirectToMainAppAllowed;
        const isSignedOut = authState === AuthStates.unauthenticated;
        const isSignedIn = checkIsSignedIn(authState);
        const isLoading = stateIsLoading && !errorStatus.hasError;
        const isMiniAppRoute = MINI_APPS_PATH_NAMES_LIST.includes(location.pathname as MiniAppRoutes);
        const showMiniApps = isMiniAppRoute;
        const showAuthPage = !IS_WSO
            && isSignedOut
            && !isMiniAppRoute
            && !hasCode
            && !isAutoSignPreviouslyInitiated;
        const showMainApp = isSignedIn
            && isFullSetUp
            && !isMiniAppRoute
            && !isLoading
            && !hasRedirectToMiniAppPrecondition;
        const showWSOError = IS_WSO && WSOAuthError.hasError;
        const isCrossOriginAuth = location.pathname === CROSS_ORIGIN_OAUTH;

        const isNothingToShow = !showMiniApps && !showAuthPage && !showMainApp && !showWSOError && !blockingPopup;
        const showLoader = (isLoading && !showMiniApps) || isNothingToShow;

        return (
            <div className="app-wrapper">
                {showLoader && <Loading fullScreen loadingText={i18n.t('general.waitMessages.pleaseWait')} />}
                {isCrossOriginAuth
                    ? <CrossOriginAuthenticator />
                    : (
                        <>
                            {showAuthPage && (
                                <div className={styles['auth-page-wrapper']}>
                                    <GoogleOAuthProvider clientId={clientId}>
                                        <AuthContainer {...authenticatorProps} />
                                    </GoogleOAuthProvider>
                                </div>
                            )}
                            {showMainApp &&
                                <ConfigProvider theme={{ hashed: false }}>
                                    <App />
                                </ConfigProvider>
                            }
                            {showMiniApps && (
                                <MiniAppsContainer
                                    isRootLoading={stateIsLoading}
                                />
                            )}
                            {showWSOError && (
                                <ErrorMessage
                                    className="app-screen"
                                    title={i18n.t(WSOAuthError.title)}
                                    subtitle={i18n.t(WSOAuthError.subtitle)}
                                />
                            )}
                        </>
                    )}
                <BlockingPopup />
            </div>
        );
    }
}

export default withTranslation(withRouter(AppAuthenticator));
